import type { AppContextState, AppSubContextType } from "../../types/app-context"

const reportedHoursScopeParamBySubContextType = {
  member: "membershipId",
  customer: "customerId",
  workspace: "workspaceId",
} as const

type ReportedHoursScopedSubContext = AppSubContextType & {
  type: keyof typeof reportedHoursScopeParamBySubContextType
}

const isReportedHoursScopedSubContext = (
  subContext: AppSubContextType | null
): subContext is ReportedHoursScopedSubContext => {
  return Boolean(subContext && subContext.type in reportedHoursScopeParamBySubContextType)
}

export const makeReportedHoursScopeQuery = ({
  appContext,
  membershipId,
}: {
  appContext: AppContextState
  membershipId?: string | null
}): ReportedHoursScopeQuery => {
  if (membershipId) return { membershipId }

  // TODO: THIS IS CAUSING THE GRID TO DISPLAY INCORRECT DATA
  // An explicit null means that the member filter was cleared. In that case,
  // return to the organisation scope instead of falling back to a member sub-context.
  if (membershipId === null && appContext.mainContext?.type === "org") {
    return { orgId: appContext.mainContext.id }
  }

  if (isReportedHoursScopedSubContext(appContext.subContext)) {
    const scopeParam = reportedHoursScopeParamBySubContextType[appContext.subContext.type]
    return { [scopeParam]: appContext.subContext.id } as ReportedHoursScopeQuery
  }

  if (appContext.mainContext?.type === "org") return { orgId: appContext.mainContext.id }

  return {}
}

type EmptyReportedHoursScopeQuery = {
  orgId?: never
  membershipId?: never
  customerId?: never
  workspaceId?: never
  projectId?: never
  taskId?: never
}

type OrgReportedHoursScopeQuery = {
  orgId: string
  membershipId?: never
  customerId?: never
  workspaceId?: never
  projectId?: never
  taskId?: never
}

type MembershipReportedHoursScopeQuery = {
  orgId?: never
  membershipId: string
  customerId?: never
  workspaceId?: never
  projectId?: never
  taskId?: never
}

type CustomerReportedHoursScopeQuery = {
  orgId?: never
  membershipId?: never
  customerId: string
  workspaceId?: never
  projectId?: never
  taskId?: never
}

type WorkspaceReportedHoursScopeQuery = {
  orgId?: never
  membershipId?: never
  customerId?: never
  workspaceId: string
  projectId?: never
  taskId?: never
}

type ProjectReportedHoursScopeQuery = {
  orgId?: never
  membershipId?: never
  customerId?: never
  workspaceId?: never
  projectId: string
  taskId?: never
}

type TaskReportedHoursScopeQuery = {
  orgId?: never
  membershipId?: never
  customerId?: never
  workspaceId?: never
  projectId?: never
  taskId: string
}

export type ReportedHoursScopeQuery =
  | EmptyReportedHoursScopeQuery
  | OrgReportedHoursScopeQuery
  | MembershipReportedHoursScopeQuery
  | CustomerReportedHoursScopeQuery
  | WorkspaceReportedHoursScopeQuery
  | ProjectReportedHoursScopeQuery
  | TaskReportedHoursScopeQuery
